import React, { useState, FormEvent, useRef, useEffect } from 'react';
import { FaUserCircle, FaCopy, FaCheck, FaCreditCard, FaCalendarAlt, FaArrowRight, FaCcVisa, FaCcMastercard, FaCcAmex, FaCcDiscover, FaHistory, FaDownload, FaEdit } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import { DashboardApi } from '../../../apis/DashboardApi';
import { AxiosResponse } from 'axios';
import Navbar from '../../Navbars/UserNavbar';
import Sidebar from '../../Sidebar/Sidebar';
import { useDispatch, useSelector } from "react-redux";
import { login } from "../../../reducer/loginSlice";
import PaymentMethodForm from './components/PaymentMethodForm';

interface FormData {
    first_name: string;
    last_name: string;
    email: string;
    phone_number: string;
    country_code: string;
    profile_image?: File;
    zip_postal_code: string;
    referral_code?: string;
}

interface UserData {
    first_name: string;
    last_name: string;
    email: string;
    phone_number: string;
    country_code: string;
    profile_image?: any;
    zip_postal_code: string;
    referral_code?: string;
}

interface Country {
    code: string;
    country_name: string;
}

interface Subscription {
    id: number;
    plan_name: string;
    status: string;
    billing_period: string;
    amount: number;
    currency: string;
    start_date: string;
    end_date: string;
    next_billing_date: string;
    payment_method: string;
    card_number?: string;
    auto_renew: boolean;
    is_lifetime?: boolean;
}

interface Transaction {
    id: number;
    date: string;
    reference: string;
    description: string;
    amount: number;
    currency: string;
    status: string;
    has_receipt: boolean;
}

const UserSettings: React.FC = () => {
    const dispatch = useDispatch();
    const userLoginData = useSelector((state: any) => state.data.login.value);
    const isLifetime = userLoginData?.data?.user_plan?.is_lifetime === true;
    const [userData, setUserData] = useState<UserData>({
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        country_code: '',
        zip_postal_code: '',
        referral_code: '',
    });
    const [subscription, setSubscription] = useState<Subscription | null>(null);
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [countries, setCountries] = useState<Country[]>([]);
    const [profile, setuserProfile] = useState<any>([]);
    const [updating, setUpdating] = useState(false);
    const [previewImage, setPreviewImage] = useState<any>('');
    const [copied, setCopied] = useState(false);
    const [activeTab, setActiveTab] = useState('profile'); // 'profile', 'subscription', or 'billing'
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const [loadingSubscription, setLoadingSubscription] = useState(false);
    const [loadingTransactions, setLoadingTransactions] = useState(false);
    const [processingAction, setProcessingAction] = useState<string | null>(null);
    
    const referralUrlRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();

    // Initial data loading
    useEffect(() => {
        fetchUserData();
        if (activeTab === 'subscription') {
            fetchSubscriptionData();
        } else if (activeTab === 'billing') {
            fetchTransactionsData();
        }
    }, [activeTab]);

    const fetchUserData = async () => {
        try {
            const response: AxiosResponse<any> = await DashboardApi.getUserData();
            setUserData(response?.data?.data?.user);
            setCountries(response?.data?.data?.countries);
            setPreviewImage(response?.data?.data?.userProfile?.image_url);
        } catch (error: any) {
            console.error('Error fetching user data:', error.response?.data || error);
            toast.error('Error fetching user data');
        }
    };
    
    const fetchSubscriptionData = async () => {
        setLoadingSubscription(true);
        try {
            const response: AxiosResponse<any> = await DashboardApi.getUserSubscription();
            if (response?.data?.status === 'success') {
                setSubscription(response?.data?.data?.subscription);
            }
        } catch (error: any) {
            console.error('Error fetching subscription data:', error.response?.data || error);
            // Don't show error toast for subscription - it might not exist
        } finally {
            setLoadingSubscription(false);
        }
    };
    
    const fetchTransactionsData = async () => {
        setLoadingTransactions(true);
        try {
            const response: AxiosResponse<any> = await DashboardApi.getBillingHistory();
            if (response?.data?.status === 'success') {
                setTransactions(response?.data?.data?.transactions || []);
            }
        } catch (error: any) {
            console.error('Error fetching transactions:', error.response?.data || error);
            toast.error('Error loading billing history');
        } finally {
            setLoadingTransactions(false);
        }
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setUserData({ ...userData, profile_image: file });
            setPreviewImage(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        const formData = new FormData();
        formData.append('first_name', userData.first_name);
        formData.append('last_name', userData.last_name);
        formData.append('email', userData.email);
        formData.append('phone_number', userData.phone_number);
        formData.append('country_code', userData.country_code);
        formData.append('zip_postal_code', userData.zip_postal_code);

        if (userData.profile_image) {
            formData.append('profile_image', userData.profile_image);
        } else {
            formData.append('profile_image', previewImage ? previewImage : null);
        }

        try {
            setUpdating(true);
            const response: any = await DashboardApi.updateUserData(formData);
            toast.success('User settings updated successfully');
        } catch (error: any) {
            console.error('Error updating user data:', error.response?.data || error);
            toast.error('Error updating user settings');
        } finally {
            setUpdating(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setUserData((prev) => ({ ...prev, [name]: value }));
    };

    const copyReferralUrl = () => {
        if (referralUrlRef.current) {
            referralUrlRef.current.select();
            document.execCommand('copy');
            // Modern clipboard API (more secure but needs user permission)
            if (navigator.clipboard) {
                navigator.clipboard.writeText(referralUrlRef.current.value)
                    .then(() => {
                        setCopied(true);
                        toast.success('Referral link copied to clipboard!');
                        
                        // Reset the copied state after 2 seconds
                        setTimeout(() => {
                            setCopied(false);
                        }, 2000);
                    })
                    .catch(err => {
                        console.error('Failed to copy: ', err);
                        toast.error('Failed to copy referral link');
                    });
            } else {
                // Fallback for browsers without clipboard API
                try {
                    document.execCommand('copy');
                    setCopied(true);
                    toast.success('Referral link copied to clipboard!');
                    
                    // Reset the copied state after 2 seconds
                    setTimeout(() => {
                        setCopied(false);
                    }, 2000);
                } catch (err) {
                    console.error('Failed to copy: ', err);
                    toast.error('Failed to copy referral link');
                }
            }
        }
    };
    
    const formatDate = (dateString: string) => {
        if (!dateString) return 'N/A';
        
        const options: Intl.DateTimeFormatOptions = { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        };
        
        return new Date(dateString).toLocaleDateString(undefined, options);
    };
    
    const toggleAutoRenew = async () => {
        if (!subscription) return;
        
        setProcessingAction('autorenew');
        try {
            const response:any = await DashboardApi.toggleAutoRenew();
            
            if (response.data.status === 'success') {
                setSubscription({
                    ...subscription,
                    auto_renew: !subscription.auto_renew
                });
                
                toast.success(subscription.auto_renew ? 
                    'Auto-renewal disabled successfully' : 
                    'Auto-renewal enabled successfully'
                );
            }
        } catch (error: any) {
            console.error('Error toggling auto-renewal:', error);
            toast.error('Failed to update auto-renewal settings');
        } finally {
            setProcessingAction(null);
        }
    };
    
    const handleCancelSubscription = async () => {
        if (!subscription) return;
        
        if (window.confirm('Are you sure you want to cancel your subscription? This action cannot be undone.')) {
            setProcessingAction('cancel');
            try {
                const response:any = await DashboardApi.cancelSubscription();
                
                if (response.data.status === 'success') {
                    setSubscription({
                        ...subscription,
                        status: 'cancelled',
                        auto_renew: false
                    });
                    
                    toast.success('Subscription cancelled successfully');
                }
            } catch (error: any) {
                console.error('Error cancelling subscription:', error);
                toast.error('Failed to cancel subscription');
            } finally {
                setProcessingAction(null);
            }
        }
    };
    
    const handleUpgradeSubscription = () => {
        navigate('/upgrade'); // Navigate to plans/pricing page
    };
    
    const handleDownloadReceipt = async (transactionId: number) => {
        try {
            // Set the transaction as processing
            const updatedTransactions = transactions.map(t => {
                if (t.id === transactionId) {
                    return { ...t, downloading: true };
                }
                return t;
            });
            setTransactions(updatedTransactions);
            
            const response:any = await DashboardApi.downloadInvoice(transactionId.toString());
            
            // Create a blob from the response data
            const blob = new Blob([response.data], { type: 'application/pdf' });
            
            // Create a URL for the blob
            const url = URL.createObjectURL(blob);
            
            // Create a temporary link and trigger the download
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `receipt-${transactionId}.pdf`);
            document.body.appendChild(link);
            link.click();
            
            // Clean up
            URL.revokeObjectURL(url);
            document.body.removeChild(link);
            
            // Reset downloading state
            const resetTransactions = transactions.map(t => {
                if (t.id === transactionId) {
                    return { ...t, downloading: false };
                }
                return t;
            });
            setTransactions(resetTransactions);
        } catch (error) {
            console.error('Error downloading receipt:', error);
            toast.error('Failed to download receipt');
            
            // Reset downloading state on error
            const resetTransactions = transactions.map(t => {
                if (t.id === transactionId) {
                    return { ...t, downloading: false };
                }
                return t;
            });
            setTransactions(resetTransactions);
        }
    };
    
    const getCardIcon = (cardType: string) => {
        switch (cardType?.toLowerCase()) {
            case 'visa':
                return <FaCcVisa className="text-blue-600 text-xl" />;
            case 'mastercard':
                return <FaCcMastercard className="text-red-600 text-xl" />;
            case 'amex':
                return <FaCcAmex className="text-blue-400 text-xl" />;
            case 'discover':
                return <FaCcDiscover className="text-orange-600 text-xl" />;
            default:
                return <FaCreditCard className="text-gray-600" />;
        }
    };
    
    const handlePaymentMethodSuccess = () => {
        setShowPaymentForm(false);
        fetchSubscriptionData();
        toast.success('Payment method updated successfully');
    };

    // Generate the full referral URL
    const referralUrl = userData.referral_code 
        ? `${window.location.origin}/sign-up?ref=${userData.referral_code}`
        : '';

    return (
        <div className="min-h-screen bg-white">
            <div className="flex flex-col md:flex-row">
                <Sidebar />
                <main className="flex-1 p-6 relative md:ml-64">
                    <Navbar />

                    <div className="md:ml-20 max-w-3xl">
                        <h1 className="text-2xl font-bold mb-6">Settings</h1>
                        
                        {/* Tabs */}
                        <div className="flex mb-6 border-b">
                            <button 
                                className={`px-4 py-2 font-medium ${activeTab === 'profile' ? 'text-orange-500 border-b-2 border-orange-500' : 'text-gray-500'}`}
                                onClick={() => setActiveTab('profile')}
                            >
                                Profile Settings
                            </button>
                            <button 
                                className={`px-4 py-2 font-medium ${activeTab === 'subscription' ? 'text-orange-500 border-b-2 border-orange-500' : 'text-gray-500'}`}
                                onClick={() => setActiveTab('subscription')}
                            >
                                Subscription
                            </button>
                            {!isLifetime && (
                                <button
                                    className={`px-4 py-2 font-medium ${activeTab === 'billing' ? 'text-orange-500 border-b-2 border-orange-500' : 'text-gray-500'}`}
                                    onClick={() => setActiveTab('billing')}
                                >
                                    Billing History
                                </button>
                            )}
                        </div>
                        
                        {/* Profile Tab */}
                        {activeTab === 'profile' && (
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="flex justify-center">
                                    <div className="relative">
                                        <img
                                            src={previewImage || 'images/profile_image.jpg'}
                                            alt="Profile"
                                            className="w-32 h-32 rounded-full object-cover"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => document.getElementById('image-upload')?.click()}
                                            className="bg-orange-500 text-white text-[10px] px-4 py-2 rounded-lg absolute -bottom-4 left-1/2 -translate-x-1/2"
                                        >
                                            Update Image
                                        </button>
                                        <input
                                            id="image-upload"
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={handleImageChange}
                                        />
                                    </div>
                                </div>

                                {/* Referral Section */}
                                <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
                                    <h2 className="text-lg font-bold mb-3">Your Referral Code</h2>
                                    <p className="text-sm text-gray-600 mb-3">
                                        Share this link with friends and family to invite them to bETERNAL.
                                    </p>
                                    
                                    <div className="flex flex-col md:flex-row gap-2">
                                        <div className="relative flex-grow">
                                            <input
                                                ref={referralUrlRef}
                                                type="text"
                                                value={referralUrl}
                                                readOnly
                                                className="w-full p-3 pr-12 rounded-lg border bg-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                                            />
                                            <button
                                                type="button"
                                                onClick={copyReferralUrl}
                                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-orange-500 transition-colors"
                                            >
                                                {copied ? <FaCheck className="text-green-500" /> : <FaCopy />}
                                            </button>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={copyReferralUrl}
                                            className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-3 rounded-lg transition-colors whitespace-nowrap"
                                        >
                                            {copied ? 'Copied!' : 'Copy Link'}
                                        </button>
                                    </div>
                                    
                                    {userData.referral_code && (
                                        <div className="mt-2 flex items-center">
                                            <span className="text-sm font-semibold mr-2">Your Referral Code:</span>
                                            <span className="bg-gray-200 px-3 py-1 rounded text-sm font-mono">{userData.referral_code}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="grid md:grid-cols-2 gap-6">
                                    <div>
                                        <label htmlFor="firstName" className="block font-bold mb-2">First Name</label>
                                        <input
                                            name="first_name"
                                            value={userData.first_name}
                                            onChange={handleInputChange}
                                            className="w-full p-3 rounded-lg border"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="lastName" className="block font-bold mb-2">Last Name</label>
                                        <input
                                            name="last_name"
                                            value={userData.last_name}
                                            onChange={handleInputChange}
                                            className="w-full p-3 rounded-lg border"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="grid md:grid-cols-2 gap-6">
                                    <div>
                                        <label htmlFor="email" className="block font-bold mb-2">Email</label>
                                        <input
                                            name="email"
                                            value={userData.email}
                                            onChange={handleInputChange}
                                            type="email"
                                            className="w-full p-3 rounded-lg border"
                                            disabled
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="phoneNumber" className="block font-bold mb-2">Phone Number</label>
                                        <input
                                            name="phone_number"
                                            value={userData.phone_number}
                                            onChange={handleInputChange}
                                            className="w-full p-3 rounded-lg border"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="flex justify-center items-center">
                                    <button
                                        type="submit"
                                        disabled={updating}
                                        className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-lg"
                                    >
                                        {updating ? 'Updating...' : 'Update Profile'}
                                    </button>
                                </div>
                            </form>
                        )}
                        
                        {/* Subscription Tab */}
                        {activeTab === 'subscription' && (
                            showPaymentForm ? (
                                <PaymentMethodForm 
                                    onSuccess={handlePaymentMethodSuccess}
                                    onCancel={() => setShowPaymentForm(false)}
                                />
                            ) : (
                                <div className="space-y-6">
                                    {loadingSubscription ? (
                                        <div className="flex justify-center py-16">
                                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
                                        </div>
                                    ) : (
                                        /* Subscription Card */
                                        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow">
                                            <div className="p-6">
                                                <h2 className="text-xl font-bold mb-4">Your Subscription</h2>
                                                
                                                {subscription ? (
                                                    <div className="space-y-4">
                                                        {/* Subscription Status */}
                                                        <div className="flex items-center">
                                                            <div className={`h-3 w-3 rounded-full mr-2 ${
                                                                subscription.status === 'active' ? 'bg-green-500' :
                                                                subscription.status === 'cancelled' ? 'bg-red-500' :
                                                                subscription.status === 'pending' ? 'bg-yellow-500' : 'bg-gray-500'
                                                            }`}></div>
                                                            <span className="font-medium capitalize">{subscription.status}</span>
                                                            {isLifetime && (
                                                                <span className="ml-3 bg-amber-100 text-amber-800 text-xs font-semibold px-2.5 py-1 rounded-full">
                                                                    Lifetime
                                                                </span>
                                                            )}
                                                        </div>

                                                        <div className="flex flex-col md:flex-row justify-between">
                                                            <div className="mb-4 md:mb-0">
                                                                <div className="text-sm text-gray-500">Current Plan</div>
                                                                <div className="text-xl font-bold">{subscription.plan_name}</div>
                                                                {!isLifetime && (
                                                                    <div className="text-md text-gray-700">
                                                                        {subscription.amount} {subscription.currency} / {subscription.billing_period}
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {!isLifetime && (
                                                                <div>
                                                                    <div className="text-sm text-gray-500">Renewal Date</div>
                                                                    <div className="text-lg font-medium">
                                                                        {formatDate(subscription.end_date)}
                                                                    </div>
                                                                    {subscription.auto_renew && (
                                                                        <div className="text-xs text-green-600">Auto-renews on this date</div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Subscription Details */}
                                                        <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-600">Subscription started</span>
                                                                <span className="font-medium">{formatDate(subscription.start_date)}</span>
                                                            </div>

                                                            {!isLifetime && (
                                                                <div className="flex justify-between">
                                                                    <span className="text-gray-600">Next billing date</span>
                                                                    <span className="font-medium">{formatDate(subscription.next_billing_date)}</span>
                                                                </div>
                                                            )}

                                                            {!isLifetime && (
                                                                <div className="flex justify-between">
                                                                    <span className="text-gray-600">Payment method</span>
                                                                    <span className="font-medium flex items-center">
                                                                        {getCardIcon(subscription.payment_method)}
                                                                        <span className="ml-2">
                                                                            {subscription.card_number ? subscription.card_number : 'Credit Card'}
                                                                        </span>
                                                                        <button
                                                                            className="ml-2 text-orange-500 hover:text-orange-600"
                                                                            onClick={() => setShowPaymentForm(true)}
                                                                            title="Update payment method"
                                                                        >
                                                                            <FaEdit size={14} />
                                                                        </button>
                                                                    </span>
                                                                </div>
                                                            )}

                                                            {!isLifetime && (
                                                                <div className="flex justify-between">
                                                                    <span className="text-gray-600">Auto-renew</span>
                                                                    <div className="flex items-center">
                                                                        {subscription.auto_renew ? (
                                                                            <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">Enabled</span>
                                                                        ) : (
                                                                            <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">Disabled</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                        
                                                        <div className="flex flex-col md:flex-row gap-4 pt-4">
                                                            {/* <button
                                                                onClick={toggleAutoRenew}
                                                                disabled
                                                                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
                                                            >
                                                                {processingAction === 'autorenew' ? (
                                                                    <>
                                                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500 inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                        </svg>
                                                                        Processing...
                                                                    </>
                                                                ) : (
                                                                    subscription.auto_renew ? 'Disable Auto-Renew' : 'Enable Auto-Renew'
                                                                )}
                                                            </button> */}
                                                            
                                                            {subscription.status === 'active' && !isLifetime && (
                                                                <button
                                                                    onClick={handleUpgradeSubscription}
                                                                    className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 font-medium flex items-center justify-center"
                                                                >
                                                                    Upgrade Plan <FaArrowRight className="ml-2" />
                                                                </button>
                                                            )}

                                                            {subscription.status === 'active' && !isLifetime && (
                                                                <button
                                                                    onClick={handleCancelSubscription}
                                                                    disabled={processingAction === 'cancel'}
                                                                    className="px-4 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 font-medium"
                                                                >
                                                                    {processingAction === 'cancel' ? (
                                                                        <>
                                                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-red-500 inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                            </svg>
                                                                            Processing...
                                                                        </>
                                                                    ) : 'Cancel Subscription'}
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="text-center py-8">
                                                        <div className="text-gray-400 mb-4">
                                                            <FaCreditCard className="mx-auto h-12 w-12" />
                                                        </div>
                                                        <h3 className="text-lg font-medium text-gray-900 mb-2">No active subscription</h3>
                                                        <p className="text-gray-600 mb-6">You don't have an active subscription at the moment.</p>
                                                        <button
                                                            onClick={handleUpgradeSubscription}
                                                            className="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-lg"
                                                        >
                                                            View Plans
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )
                        )}
                        
                        {/* Billing History Tab */}
                        {activeTab === 'billing' && !isLifetime && (
                            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow">
                                <div className="p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <h2 className="text-xl font-bold">Billing History</h2>
                                        <div className="flex items-center text-gray-500 text-sm">
                                            <FaHistory className="mr-2" />
                                            Recent transactions first
                                        </div>
                                    </div>
                                    
                                    {loadingTransactions ? (
                                        <div className="flex justify-center py-12">
                                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
                                        </div>
                                    ) : transactions.length > 0 ? (
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead>
                                                    <tr>
                                                        <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                                        <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                                        <th className="px-6 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                                        <th className="px-6 py-3 bg-gray-50 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                                        <th className="px-6 py-3 bg-gray-50 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {transactions.map((transaction:any) => (
                                                        <tr key={transaction.id} className="hover:bg-gray-50">
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                {new Date(transaction.date).toLocaleDateString()}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {transaction.description}
                                                                <div className="text-xs text-gray-500">Ref: {transaction.reference}</div>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium">
                                                                {transaction.currency} {transaction.amount}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-center">
                                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium 
                                                                    ${transaction.status === 'paid' || transaction.status === 'Successful' 
                                                                        ? 'bg-green-100 text-green-800' 
                                                                        : transaction.status === 'pending' 
                                                                        ? 'bg-yellow-100 text-yellow-800' 
                                                                        : 'bg-red-100 text-red-800'}`}
                                                                >
                                                                    {transaction.status === 'Successful' ? 'Paid' : transaction.status}
                                                                </span>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                                                {transaction.has_receipt && (
                                                                    <button 
                                                                        onClick={() => handleDownloadReceipt(transaction.id)}
                                                                        disabled={transaction.downloading}
                                                                        className="text-orange-600 hover:text-orange-900 flex items-center justify-center mx-auto"
                                                                    >
                                                                        {transaction.downloading ? (
                                                                            <svg className="animate-spin h-4 w-4 text-orange-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                            </svg>
                                                                        ) : (
                                                                            <>
                                                                                <FaDownload className="mr-1" /> Receipt
                                                                            </>
                                                                        )}
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="text-center py-12">
                                            <FaHistory className="mx-auto h-12 w-12 text-gray-300 mb-4" />
                                            <h3 className="text-lg font-medium text-gray-900 mb-2">No transactions yet</h3>
                                            <p className="text-gray-500">Your billing history will appear here once you have made a payment.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </main>
            </div>
            <ToastContainer
                position="top-right"
                autoClose={2000}
                hideProgressBar={true}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
        </div>
    );
};

export default UserSettings;